// Defines Sentinel API DTOs and UI models.

// ---------- Existing Sentinel backend wire DTOs ----------

export type SentinelModeDto = 'review' | 'live';
export type SentinelProviderDto =
  | 'gorgias'
  | 'zendesk'
  | 'zoho_desk'
  | 'richpanel'
  | 'intercom'
  | 'helpdesk'
  | 'helpscout'
  | 'freshdesk'
  | 'gladly'
  | 'kustomer';

/** Providers whose agents a live ticket can be handed back to. Mirrors the backend's handoff set. */
const HANDOFF_PROVIDERS: ReadonlySet<SentinelProviderDto> = new Set([
  'gorgias',
  'intercom',
  'zendesk',
  'richpanel',
  'zoho_desk',
  'helpscout',
  'kustomer',
  'freshdesk',
  'gladly',
]);

export const supportsProviderHandoff = (provider: SentinelProviderDto | undefined): boolean =>
  provider !== undefined && HANDOFF_PROVIDERS.has(provider);

/**
 * Providers whose live replies can carry files. Mirrors the backend's outbound-attachment set.
 * Gladly is left out: its public API has no way to attach a file to an outbound reply.
 */
const ATTACHMENT_PROVIDERS: ReadonlySet<SentinelProviderDto> = new Set([
  'gorgias',
  'intercom',
  'zendesk',
  'richpanel',
  'zoho_desk',
  'helpscout',
  'kustomer',
  'freshdesk',
]);

export const supportsProviderAttachments = (provider: SentinelProviderDto | undefined): boolean =>
  provider !== undefined && ATTACHMENT_PROVIDERS.has(provider);

/**
 * Providers whose customer history cannot be linked: Richpanel's customer lookup returns opaque
 * conversation ids, not the conversation number its ticket links need, so no row would open.
 */
const UNLINKABLE_CUSTOMER_TICKET_PROVIDERS: ReadonlySet<SentinelProviderDto> = new Set(['richpanel']);

export const supportsCustomerTickets = (provider: SentinelProviderDto | undefined): boolean =>
  provider !== undefined && !UNLINKABLE_CUSTOMER_TICKET_PROVIDERS.has(provider);

export interface SentinelContactWireDto {
  id: number;
  name: string | null;
  email: string | null;
}

export interface SentinelConversationWireDto {
  id: number;
  /**
   * The brand this conversation belongs to. Null is account-level, which is every
   * conversation predating the inbox-to-brand mapping.
   *
   * A BIGINT, so the driver hands it back as a string. Compared against the brand list's
   * ids, which are strings for the same reason.
   *
   * OPTIONAL, not required. A backend that has not shipped this field yet sends a payload
   * without it, and the adapter already reads an absent value as "no brand" — declaring it
   * required only breaks the build against older fixtures without making the runtime safer.
   */
  bu_id?: string | number | null;
  display_id: number;
  external_id: string | null;
  categories: string[] | null;
  cached_label_list: string | null;
  additional_attributes: Record<string, unknown> | null;
  custom_attributes: Record<string, unknown> | null;
  created_at: string;
  updated_at: string;
  contact?: SentinelContactWireDto | null;
}

export interface SentinelMessageWireDto {
  id: number;
  content: string | null;
  processed_message_content: string | null;
  message_type: number;
  sender_type: string | null;
  private: boolean;
  content_attributes: Record<string, unknown> | null;
  additional_attributes: Record<string, unknown> | null;
  /** The helpdesk's id for the message; absent from a backend that does not send it yet. */
  source_id?: string | null;
  created_at: string;
  attachments?: SentinelMessageAttachmentWireDto[];
  /** Kim sent it on its own (the automation reply log has it); absent from older backends. */
  automated?: boolean;
}

export interface SentinelMessageAttachmentWireDto {
  index: number;
  fileName: string;
  fileType: 'image' | 'audio' | 'video' | 'file';
  contentType: string | null;
  fileSize: number | null;
}

export interface SentinelOutboundAttachmentWireDto {
  key: string;
  fileName: string;
  contentType: string;
  fileSize: number;
}

export interface SentinelOutboundAttachment {
  key: string;
  fileName: string;
  contentType: string;
  fileSize: number;
}

export interface SentinelTicketWireDto {
  id: number;
  account_id: number;
  conversation_id: number;
  source_provider: SentinelProviderDto;
  source_ticket_id: string;
  mode: SentinelModeDto;
  status: string;
  latest_message_id: number | null;
  root_trace_id: string | null;
  current_ai_event_log_id: number | null;
  provider_message_id: string | null;
  active_generation_trace_id: string | null;
  active_generation_request_id: string | null;
  blueprint_retries_used: number;
  response_retries_used: number;
  last_error_code: string | null;
  last_error_detail: string | null;
  created_at: string;
  updated_at: string;
  conversation: SentinelConversationWireDto;
  latestMessage: SentinelMessageWireDto | null;
  currentAiEventLog?: SentinelAiEventLogWireDto | null;
  /** Auto-routing. Absent before the backend ships it. */
  router_state?: 'unassigned' | 'open' | 'closed' | null;
  assigned_user_id?: number | null;
  owner?: { id: number; name: string | null; display_name: string | null } | null;
}

export interface SentinelAiEventLogWireDto {
  id: number;
  trace_id: string;
  request_id: string | null;
  root_trace_id: string | null;
  parent_event_log_id: number | null;
  event_kind: 'request' | 'blueprint_revision';
  blueprint_revision_number: number | null;
  response_message: string | null;
  confidence_score?: number | null;
  /** Ids Brahma reported for the turn; names resolve through the by-ids endpoints. */
  knowledge_base_list: string[] | null;
  rule_list: string[] | null;
  /** Conversation ids the V2 RAG match named; superseded by extra_details.v3_past_similar_tickets. */
  similar_ticket_ids?: string[] | null;
  approval_requests: Record<string, unknown> | null;
  /** Post-processing actions for this turn — absent on a turn that has none. */
  pending_actions?: PendingActionsDto | null;
  extra_details: Record<string, unknown> | null;
  created_at: string;
}

/** A tag / close / status change a workflow branch attached to its reply; Kim runs it on an automatic reply, a person otherwise. */
export interface PendingActionDto {
  /** Brahma's own name for the action, where the caller has it; otherwise the tool's name is read out. */
  label?: string;
  tool: string;
  provider: string;
  inputs: Record<string, unknown>;
}

export interface PendingActionResultDto {
  id: string;
  tool: string;
  status: 'executed' | 'skipped' | 'failed';
  detail?: string;
}

export interface PendingActionsDto {
  plan: { version: string; actions: PendingActionDto[] };
  /** `running` while an agent's run is in progress; a partial or failed run can be run again. */
  status: 'pending' | 'running' | 'executed' | 'partial' | 'failed';
  results?: PendingActionResultDto[];
  executedAt?: string;
  executedBy?: number | null;
  trigger?: 'automation' | 'manual';
}

/** One past conversation the RAG match named, with how close Brahma scored it. */
export interface SimilarTicketMatch {
  conversationId: string;
  similarity: number | null;
}

export interface SimilarTicketMessage {
  id: number;
  role: 'customer' | 'agent';
  createdAt: string;
  text: string;
}

/** A past ticket opened from the panel: its reference fields plus the whole thread. */
export interface SimilarTicketThreadDto {
  id: string;
  externalId: string;
  subject?: string;
  category?: string;
  ticketUrl?: string;
  messages: SimilarTicketMessage[];
}

export interface SentinelTicketListWireDto {
  rows: SentinelTicketWireDto[];
  count: number;
}

export interface SentinelWorkspaceDetailWireDto {
  ticket: SentinelTicketWireDto;
  aiEventLogs: SentinelAiEventLogWireDto[];
  messages?: SentinelMessageWireDto[];
  providerContext: Record<string, unknown> | null;
}

export interface SentinelSettingsWireDto {
  mode: SentinelModeDto;
  enabled: boolean;
  blueprint_enabled: boolean;
  provider_assignees: Partial<Record<SentinelProviderDto, string>>;
  max_blueprint_retries: number;
  max_response_retries: number;
  /** Auto-routing on/off for this brand (or the account default). */
  routing_enabled?: boolean;
  /** Whether a ticket its owner replied to (Send) still counts toward their cap. */
  send_holds_capacity?: boolean;
  inactivity_release_minutes?: number;
  /** The cap every Sentinel in the brand gets unless they have their own. Null means no cap. */
  default_max_active_tickets?: number | null;
}

/** One brand a Sentinel works, and their own cap there. */
export interface SentinelUserBrandDto {
  buId: number;
  /** Null when they follow the brand's cap for every Sentinel (which may itself be none). */
  maxActiveTickets: number | null;
}

/** A brand's cap for every Sentinel. */
export interface SentinelBrandCapDto {
  buId: number;
  /** The brand's cap, DEFAULT_MAX_ACTIVE_TICKETS when it has none set. */
  maxActiveTickets: number;
}

/** A brand the signed-in Sentinel covers. */
export interface SentinelBrandDto {
  id: string;
  name: string;
}

/** Right-panel customer block. */
export interface WorkspaceCustomerDto {
  name: string;
  email: string | null;
  segment: string | null;
  ltv: number | null;
  tier: string | null;
  history: string | null;
  topCategory: string | null;
}

export interface OrderItemDto {
  quantity: number;
  name: string;
  price: string;
}

/** Right-panel order block. */
export interface WorkspaceOrderDto {
  externalId: string;
  statuses: string[];
  orderedOn: string;
  items: OrderItemDto[];
  shippingCost: { label: string; amount: string } | null;
  /** Providers often omit the address entirely, so the whole block is optional. */
  shippingAddress: {
    name: string;
    address1: string;
    address2: string;
    city: string;
    country: string;
    zipcode: string;
  } | null;
  shipment: { provider: string; trackingId: string; link: string } | null;
  /** Order total shown beside "Ordered on". */
  totalSpent: string | null;
}

/** Right-panel storefront block — account summary plus its orders. */
export interface WorkspaceStorefrontDto {
  provider: string;
  logo: string;
  accentColor: string;
  totalOrders: number;
  totalSpent: string;
  customerSince: string | null;
  tags: string[];
  orders: WorkspaceOrderDto[];
  /** Drafts come from their own provider call, so they are tracked separately. */
  drafts: WorkspaceOrderDto[];
}

export interface LabelledValueDto {
  label: string;
  value: string;
}

/** One plan on the customer's subscription account. Null fields are simply not shown. */
export interface SubscriptionItemDto {
  id: string;
  name: string;
  status: string;
  createdOn: string;
  quantity: number | null;
  amount: string;
  /** Caption under the amount — "Total spent" on Recharge, "Amount" on Smartrr. */
  amountLabel: string;
  details: LabelledValueDto[];
  /** Line items behind the "Product Details" fold; absent when the plan is the product. */
  products?: { name: string; quantity: number | null; amount: string }[];
  cancellationReason: string | null;
  cancellationDate: string | null;
}

/** Right-panel subscription block, one per provider (Recharge, Smartrr). */
export interface WorkspaceSubscriptionDto {
  provider: string;
  /** Provider logo under /assets/brand. */
  logo: string;
  /** Provider brand colour for the left edge of the panel. */
  accentColor: string;
  count: number;
  totalSpent: string;
  meta: LabelledValueDto[];
  items: SubscriptionItemDto[];
}

/** What the API can tell us about a subscription account; the panel adds the branding. */
export type SubscriptionBlockDto = Omit<WorkspaceSubscriptionDto, 'logo' | 'accentColor'>;

/** Blueprint — camelCase wrapper + snake_case Brahma payload (promoted-blueprints pattern). */
export interface BlueprintStepDto {
  step_id: string;
  type: 'tool' | 'information_gathering' | 'llm_reasoning';
  title: string;
  description: string;
  tool_name: string | null;
  risk_level: 'low' | 'medium' | 'high' | null;
}

export interface SentinelBlueprintDto {
  blueprintId: string;
  conversationId: number;
  version: number;
  blueprint: {
    intent: string;
    reasoning: string;
    steps: BlueprintStepDto[];
    responsePolicy?: string;
    customerQuestions?: string[];
  };
}

export interface ProviderAgentDto {
  id: string;
  name: string;
  email: string | null;
}

export interface ProviderAgentPageDto {
  provider: SentinelProviderDto;
  agents: ProviderAgentDto[];
  hasMore: boolean;
  nextCursor: string | null;
}

/** A helpdesk tag as its picker shows it; `color` only where the helpdesk has one (Gorgias). */
export interface HelpdeskTagDto {
  name: string;
  color: string | null;
}

/** The helpdesk's own tags; empty for a helpdesk without a tag list, where tags are typed. */
export interface TicketTagsDto {
  provider: SentinelProviderDto;
  tags: HelpdeskTagDto[];
}

/** One of the customer's other tickets, read live from the helpdesk. */
export interface CustomerTicketDto {
  id: string;
  subject: string | null;
  status: string | null;
  createdAt: string | null;
  url: string | null;
}

export interface CustomerTicketPageDto {
  provider: SentinelProviderDto;
  tickets: CustomerTicketDto[];
  hasMore: boolean;
}

// ---------- UI models (what components render) ----------

export interface SentinelTicket {
  id: number;
  conversationId: number;
  /**
   * The brand, as an id, mirroring what the API returns. Not rendered: the queue is
   * already filtered to the selected brand, so every row in it shares one brand and a
   * chip repeating that on each row says nothing.
   */
  businessUnitId: string | null;
  sourceTicketId: string;
  displayId: string;
  subject: string;
  category: string | null;
  customerName: string;
  preview: string;
  lastActivityAt: string;
  /** Where the ticket stands for auto-routing; null when it is outside routing. */
  routerState: 'unassigned' | 'open' | 'closed' | null;
  /** The Sentinel who owns it under auto-routing; null when nobody does. */
  ownerName: string | null;
  /** That owner's user id; null when nobody owns it. */
  ownerId: number | null;
}

export interface WorkspaceMessage {
  id: number;
  markdown: string;
  from: 'customer' | 'agent';
  /** Private note — visible to agents only, rendered yellow like the helpdesk. */
  isPrivate?: boolean;
  /** Sent by automation, matched to the send that made it; never set on a private note. */
  isAutomated?: boolean;
  senderName: string;
  /** Sender photo; the UI falls back to initials when absent. */
  avatarUrl?: string | null;
  createdAt: string;
  attachments?: WorkspaceMessageAttachment[];
  /** Known email headers for a customer message. */
  email?: { from: string | null; to: string | null; subject: string | null };
  /**
   * The customer's email as the helpdesk received it, when one was stored.
   *
   * `markdown` is the CLEANED body — the helpdesk strips the signature and our own HTML-to-text
   * pass drops anything that is not text. That is the right input for the model, and the wrong
   * thing to show an operator: an email whose whole content is an image signature arrives as the
   * bare `--` delimiter and reads as an empty ticket.
   *
   * Incoming only, with the quoted reply chain already cut off (`stripQuotedTail`). Unsanitised:
   * render it through `EmailHtmlContent`, never `MarkdownContent`. An agent reply keeps `markdown`,
   * because `outgoingReplyText` on the backend strips the quoted history off it.
   */
  html?: string | null;
}

export interface WorkspaceMessageAttachment extends SentinelMessageAttachmentWireDto {
  ticketId: number;
  messageId: number;
}

/** How a finished ticket ended — each reads differently to whoever opens it next. */
export const TICKET_OUTCOME = {
  answered: 'answered',
  resolved: 'resolved',
  unassigned: 'unassigned',
  notPickedUp: 'notPickedUp',
} as const;

export type TicketOutcome = (typeof TICKET_OUTCOME)[keyof typeof TICKET_OUTCOME];

export interface SentinelConversation {
  id: number;
  conversationId: number;
  displayId: string;
  subject: string;
  category: string | null;
  messages: WorkspaceMessage[];
  customer: WorkspaceCustomerDto;
  order: WorkspaceOrderDto | null;
  subscriptions: SubscriptionBlockDto[];
  /** Null while the ticket is still being worked. */
  outcome: TicketOutcome | null;
}

export interface BlueprintStep {
  id: string;
  title: string;
  description: string;
}

export interface Blueprint {
  aiEventLogId: number;
  id: string;
  version: number;
  intent: string;
  steps: BlueprintStep[];
}

export interface ResponseSource {
  id: string;
  title: string;
  fileType: string;
  excerpt: string;
  url: string | null;
}

export interface TraceStep {
  id: string;
  kind: 'lookup' | 'policy' | 'compose';
  title: string;
  detail: string | null;
  /** The step reported that it finished. Only the live progress card reads it. */
  done: boolean;
  toolName?: string;
  toolProvider?: string;
  toolInput?: Record<string, unknown>;
  toolOutput?: unknown;
  outcome?: 'success' | 'error' | 'awaiting_approval';
  durationMs?: number;
}

export interface GeneratedResponse {
  id: string;
  version: number;
  markdown: string;
  /** Null when the turn recorded no score — render nothing rather than a zero. */
  confidencePct: number | null;
  knowledgeBaseIds: string[];
  ruleIds: string[];
  trace: TraceStep[];
  origin: ResponseOrigin;
}

export interface PendingAction {
  id: string;
  label: string;
  description: string;
  ctaLabel: string;
  progressLabel: string;
  confidencePct: number;
  sources: ResponseSource[];
}

export interface AgentOption {
  id: string;
  name: string;
  email: string | null;
}

// ---------- Shared enums / options ----------

/** How a response version came to be — compared across the workspace, never as raw strings. */
export const RESPONSE_ORIGIN = {
  generate: 'generate',
  refine: 'refine',
  postAction: 'post_action',
} as const;

export type ResponseOrigin = (typeof RESPONSE_ORIGIN)[keyof typeof RESPONSE_ORIGIN];

/** Picking these reveals the free-text input in their modals. */
export const OTHERS_REJECT_REASON = 'others';
export const OTHERS_CATEGORY = 'Others';

/** Reasons offered in every reject / unassign dropdown (from product). */
export const REJECT_REASONS = [
  { value: 'error_in_generation', label: 'Error in generation' },
  { value: 'out_of_scope', label: 'Out of scope' },
  { value: OTHERS_REJECT_REASON, label: 'Others' },
] as const;

export type RejectReason = (typeof REJECT_REASONS)[number]['value'];

/** "Change style" refine presets — the value is the backend refine command. */
export const REFINE_STYLES = [
  { value: 'shorten', label: 'Shorten' },
  { value: 'expand', label: 'Expand' },
  { value: 'friendly', label: 'Friendly' },
  { value: 'formal', label: 'Formal' },
  { value: 'empathetic', label: 'Empathetic' },
  { value: 'fix-grammar', label: 'Fix Grammar & Spelling' },
] as const;

export type RefineStyle = (typeof REFINE_STYLES)[number]['value'];

/** "Whats Wrong?" feedback options behind the Refine response button. */
export const REFINE_CATEGORIES = [
  'Wrong resolution',
  'Incomplete resolution',
  'Factually wrong',
  'Wrong brand tone and style',
  'Wrong sentiment',
  'Exception handling',
  OTHERS_CATEGORY,
] as const;

export type RefineCategory = (typeof REFINE_CATEGORIES)[number];

/** Whether the reply was good; the backend keys feedback on the same two values. */
export type FeedbackVerdict = 'yes' | 'no';

/** "Remember this" scopes offered in the send-time feedback modal. */
export const REMEMBER_SCOPES = [
  'As an exception',
  'For this scenario',
  'For this customer',
  'For this brand',
] as const;

export type RememberScope = (typeof REMEMBER_SCOPES)[number];

/** Admin-configured workspace settings (Settings → Sentinel Workspace). */
export interface SentinelSettingsDto {
  /** Sentinel dashboard on/off. */
  dashboardEnabled: boolean;
  /** Where approved responses are delivered. */
  sendResponsesTo: 'client_review_dashboard' | 'direct_to_customer';
  /** How many times a response can be refined. */
  maxResponseRefines: number;
  /** Off = tickets skip the blueprint step and generate directly. */
  blueprintEnabled: boolean;
  /** How many times a blueprint can be regenerated. */
  maxBlueprintRefines: number;
  /** Existing live provider allowlist/assignee snapshot, preserved by settings UI. */
  providerAssignees: Partial<Record<SentinelProviderDto, string>>;
  /** Auto-routing for the selected brand. */
  routingEnabled: boolean;
  /** Send keeps the ticket on its owner's cap (Send & close always frees it). */
  sendHoldsCapacity: boolean;
  /** Minutes a Sentinel may be offline in the brand before their tickets there are released. */
  inactivityReleaseMinutes: number;
  /** The cap every Sentinel in the brand gets unless they have their own. Null means the default (10). */
  defaultMaxActiveTickets: number | null;
}

/** How long a Sentinel may be offline before their tickets are released. No "off": always on. */
export const INACTIVITY_RELEASE_OPTIONS = [
  { value: 10, label: '10 minutes' },
  { value: 15, label: '15 minutes' },
  { value: 30, label: '30 minutes' },
  { value: 60, label: '1 hour' },
  { value: 120, label: '2 hours' },
  { value: 240, label: '4 hours' },
  { value: 480, label: '8 hours' },
  { value: 1440, label: '24 hours' },
] as const;

export const DEFAULT_INACTIVITY_RELEASE_MINUTES = 10;

export const SEND_RESPONSES_TO_OPTIONS = [
  { value: 'client_review_dashboard', label: 'Client review dashboard' },
  { value: 'direct_to_customer', label: 'Direct to Customer' },
] as const;

/** Product default; admins override it in Settings → Sentinel Workspace. */
export const MAX_RESPONSE_REFINES = 10;
